import type { TickRendererProps } from '@visx/axis';

import { useTickTruncation } from './hooks/useTickTruncation.js';
import { useXTickOffset } from './hooks/useXTickOffset.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { X_AXES_TICK_LINE_OFFSET } from '../../../core/components/axes/constants.js';
import { ForeignObjectWrapper } from '../../../core/components/foreign-object-wrapper/ForeignObjectWrapper.js';
import { useXYChartTruncationMode } from '../../hooks/useXYChartTruncationMode.js';

interface YTickWithTruncationProps {
  position: 'top' | 'bottom';
  renderProps: TickRendererProps;
  maxSpaceForTruncation: number;
  centerTick: boolean;
  isTickRotated: boolean;
}

export function XTickWithTruncation({
  position,
  renderProps,
  maxSpaceForTruncation,
  centerTick,
  isTickRotated,
}: YTickWithTruncationProps) {
  const { formattedValue, x: xFromProps, y: yFromProps } = renderProps;

  const { truncationMode } = useXYChartTruncationMode();

  const {
    displayValue,
    tickClassName,
    tickInlineStyle,
    truncatedHeight,
    truncatedWidth,
    truncatedValue,
  } = useTickTruncation(formattedValue, maxSpaceForTruncation, truncationMode);

  const { xOffset, yOffset, style } = useXTickOffset(
    truncatedWidth,
    truncatedHeight,
    renderProps,
    centerTick,
    isTickRotated,
    position,
  );

  const showTooltip = truncatedValue === formattedValue;

  const x = isTickRotated
    ? position === 'top'
      ? X_AXES_TICK_LINE_OFFSET
      : -(truncatedWidth + X_AXES_TICK_LINE_OFFSET)
    : xFromProps - xOffset;

  const y = isTickRotated
    ? xFromProps - (centerTick ? yOffset : xOffset)
    : yFromProps - yOffset;

  return (
    <ForeignObjectWrapper x={x} y={y} style={style}>
      <Tooltip disabled={showTooltip} text={formattedValue}>
        <span className={tickClassName} style={tickInlineStyle}>
          {displayValue}
        </span>
      </Tooltip>
    </ForeignObjectWrapper>
  );
}
